import { Allowed_CSS_Properties, Allowed_HTML_Attributes, Allowed_HTML_Tags, Allowed_IDs } from "@ms/uno-constants/lib/local/DOMPurifyConstants";
import DOMPurify from "dompurify";
const EmptyStyleAttributePattern = /\sstyle\s*=\s*(['"])\s*\1/gi;
/**
 * Function used to sanitize content
 * @param content The original content being sanitized
 * @param customConfig Optional custom configuration for sanitization
 */ export function sanitizeContent(content, customConfig) {
    return sanitizeInternal(content, customConfig);
}
/**
 * Function used to sanitize content and return TrustedHTML if Trusted Types is enabled
 * @param content The original content being sanitized
 * @param customConfig Optional custom configuration for sanitization
 */ export function sanitizeForTrustedTypes(content, customConfig) {
    const config = customConfig ? {
        ...customConfig,
        returnTrustedTypes: true
    } : {
        returnTrustedTypes: true
    };
    return sanitizeInternal(content, config);
}
// Internal function to sanitize content with optional custom configuration
function sanitizeInternal(content, customConfig) {
    setUpDOMPurifyConfigs(customConfig);
    const sanitizedContent = DOMPurify.sanitize(content.replace(/&nbsp;/g, " "));
    removeDOMPurifyHooksAndConfig();
    return removeEmptyStyleAttributes(sanitizedContent);
}
function removeEmptyStyleAttributes(sanitizedContent) {
    // TrustedHTML cannot be manipulated as a string while preserving its trusted type.
    if (typeof sanitizedContent !== "string") {
        return sanitizedContent;
    }
    return sanitizedContent.replace(EmptyStyleAttributePattern, "");
}
// Helper to get a mapping of allowed css attributes to Javascript properties
function getAllowedCSSToJavascriptPropertiesMap() {
    const cssPropsMap = {};
    const toCamelCaseRegExp = new RegExp("-([a-z])", "g");
    Allowed_CSS_Properties.forEach((cssProp)=>{
        const cssJavaScriptProperty = cssProp.replace(toCamelCaseRegExp, function(g) {
            return g[1].toUpperCase();
        });
        cssPropsMap[cssJavaScriptProperty] = cssProp;
    });
    return cssPropsMap;
}
function normalizeMalformedStyleDeclarations(styleString, allowedCSSPropsMap) {
    const output = [];
    const allowedCssProperties = new Set(Object.values(allowedCSSPropsMap).map((prop)=>prop.toLocaleLowerCase("en-US")));
    const parserStyle = document.createElement("div").style;
    const normalizedStyleString = styleString.replace(/(["'])(?=[a-z-]+\s*:)/gi, "$1;");
    // Best-effort parser for malformed declarations such as: "border:1px solid color:red;"
    const declarationRegExp = /([a-z-]+)\s*:\s*([^;]+?)(?=(?:\s+[a-z-]+\s*:)|;|$)/gi;
    let declarationMatch;
    while((declarationMatch = declarationRegExp.exec(normalizedStyleString)) !== null){
        const cssProperty = declarationMatch[1]?.toLocaleLowerCase("en-US");
        const cssValue = declarationMatch[2]?.trim();
        if (!cssProperty || !cssValue || !allowedCssProperties.has(cssProperty)) {
            continue;
        }
        // Run recovered declarations through CSSOM parsing so invalid values are dropped
        // and accepted values are normalized consistently with the primary path.
        parserStyle.cssText = "";
        try {
            parserStyle.setProperty(cssProperty, cssValue);
        } catch  {
            continue;
        }
        const parsedValue = parserStyle.getPropertyValue(cssProperty).trim();
        if (!parsedValue) {
            continue;
        }
        output.push(`${cssProperty}:${parsedValue};`);
    }
    return output;
}
function hasLikelyConcatenatedStyleDeclarations(styleString) {
    let quote;
    let parenthesisDepth = 0;
    let hasDeclarationColon = false;
    for(let index = 0; index < styleString.length; index++){
        const character = styleString[index];
        if (quote) {
            if (character === "\\") {
                index++;
            } else if (character === quote) {
                quote = undefined;
            }
            continue;
        }
        if (character === '"' || character === "'") {
            quote = character;
        } else if (character === "(") {
            parenthesisDepth++;
        } else if (character === ")" && parenthesisDepth > 0) {
            parenthesisDepth--;
        } else if (parenthesisDepth === 0 && character === ";") {
            hasDeclarationColon = false;
        } else if (parenthesisDepth === 0 && character === ":") {
            if (hasDeclarationColon) {
                const precedingText = styleString.slice(0, index);
                const propertyMatch = /([a-z-]+)\s*$/i.exec(precedingText);
                const propertyStart = propertyMatch ? index - propertyMatch[0].length : -1;
                const boundaryCharacter = propertyStart > 0 ? styleString[propertyStart - 1] : undefined;
                if (boundaryCharacter && /[\s"']/.test(boundaryCharacter)) {
                    return true;
                }
            }
            hasDeclarationColon = true;
        }
    }
    return false;
}
// Helper method to set up DOMPurify configs, including settings such as allowed attributes/tags etc.
function setUpDOMPurifyConfigs(customConfig) {
    const allowedCSSPropsMap = getAllowedCSSToJavascriptPropertiesMap();
    /**
     * Below code is mostly copied from: https://github.com/cure53/DOMPurify/blob/main/demos/hooks-sanitize-css-demo.html
     * The purpose of this chunk of code is to handling css sanitization
     * DOMPurify currently doesn't have a direct config to handle css sanitization
     */ /**
     * Take CSS property-value pairs and validate against allow-list,
     * then add the styles to an array of property-value pairs
     * Note: We also compare against original fullStyleString to ensure we're not adding CSS properties that didn't exist in original
     */ function validateStyles(output, styles, fullStyleString) {
        if (!styles || !fullStyleString) {
            return;
        }
        const originalFullStyleString = fullStyleString;
        // We want case-insensitive comparison for CSS properties (and, "en-US" so we don't have incorrect lowercasing in other locales)
        fullStyleString = fullStyleString.toLocaleLowerCase("en-US");
        // Validate regular CSS properties
        for(const prop in styles){
            if (typeof styles[prop] === "string") {
                const allowedCSSProperty = allowedCSSPropsMap[prop];
                const hasExactDeclaration = fullStyleString.split(";").some((declaration)=>{
                    const colonIndex = declaration.indexOf(":");
                    return colonIndex >= 0 && declaration.slice(0, colonIndex).trim() === allowedCSSProperty;
                });
                const propertyExistsInOriginalStyle = hasExactDeclaration || !styles[prop].toLocaleLowerCase("en-US").includes("url(") && fullStyleString.includes(allowedCSSProperty);
                if (styles[prop] && allowedCSSProperty && propertyExistsInOriginalStyle) {
                    output.push(allowedCSSPropsMap[prop] + ":" + styles[prop] + ";");
                }
            }
        }
        if (hasLikelyConcatenatedStyleDeclarations(originalFullStyleString)) {
            const normalizedDeclarations = normalizeMalformedStyleDeclarations(originalFullStyleString, allowedCSSPropsMap);
            if (normalizedDeclarations.length > 0) {
                output.splice(0, output.length, ...normalizedDeclarations);
            }
        }
    }
    // Add a hook to enforce CSS attribute sanitization
    DOMPurify.addHook("afterSanitizeAttributes", function(node) {
        // Remove all IDs that are not in the allowed list
        if (node.tagName && node.hasAttribute("id")) {
            const idValue = node.getAttribute("id");
            if (idValue !== null && !Allowed_IDs.includes(idValue)) {
                node.removeAttribute("id");
            }
        }
        // Nasty hack to fix baseURI + CSS problems in Chrome
        if (!node.ownerDocument.baseURI) {
            const base = document.createElement("base");
            base.href = document.baseURI;
            node.ownerDocument.head.appendChild(base);
        }
        // Check all style attribute values and validate them
        if (node.hasAttribute("style") && node.style != null) {
            const output = [];
            // We must cast node as any here because getAttribute("style") returns the style object as a flattened string
            validateStyles(output, node.style, node.getAttribute("style"));
            // re-add styles in case any are left
            if (output.length) {
                node.setAttribute("style", output.join(""));
            } else {
                node.removeAttribute("style");
            }
        }
        if (customConfig?.openLinksInNewTab && node.tagName?.toLocaleLowerCase("en-US") === "a") {
            node.setAttribute("target", "_blank");
            node.setAttribute("rel", "noopener noreferrer");
        }
    });
    /* END copied code */ // The following config settings specify accepted HTML tags and attributes
    // The accepted lists match with current Todo Server accepted list
    // Refer to service code todoserver/Utility/HtmlConverter.cs for allowed list
    // This setting also matches with Teams Tasks Mobile DomPurify Config setting
    // Refer to TT's mobilern/src/components/taskEditor/TaskRichTextEditor.tsx
    const config = {
        ALLOWED_TAGS: customConfig?.allowedTags ? [
            ...Allowed_HTML_Tags,
            ...customConfig.allowedTags
        ] : Allowed_HTML_Tags,
        ALLOWED_ATTR: customConfig?.allowedAttributes ? [
            ...Allowed_HTML_Attributes,
            ...customConfig.allowedAttributes
        ] : Allowed_HTML_Attributes,
        ALLOW_DATA_ATTR: false,
        ALLOW_ARIA_ATTR: false,
        RETURN_TRUSTED_TYPE: customConfig?.returnTrustedTypes ?? false
    };
    DOMPurify.setConfig(config);
}
/**
 * Function used to remove DOMPurify hooks
 */ function removeDOMPurifyHooksAndConfig() {
    DOMPurify.removeHook("afterSanitizeAttributes");
    DOMPurify.clearConfig();
}
